export const EMBED_PATH_PREFIX = '/embed';

export const EMBED_MESSAGES = {
  READY: 'alacaza-embed:ready',
  TOKEN: 'alacaza-embed:token',
  HEIGHT: 'alacaza-embed:height',
  AUTH_EXPIRED: 'alacaza-embed:auth-expired',
  // Envoyé par le parent : portion de l'iframe réellement visible, en
  // coordonnées iframe (`top` = px masqués au-dessus, `height` = hauteur
  // visible). Comme le parent dimensionne l'iframe sur toute la hauteur du
  // contenu, `position: fixed` s'ancre sur ce contenu et non sur ce que voit
  // l'utilisateur : sans cette info les modales et les toasts s'affichent hors
  // écran. Message optionnel — à défaut, tout est ancré en haut de l'iframe.
  VIEWPORT: 'alacaza-embed:viewport',
} as const;

/**
 * Hauteur plancher annoncée au parent. Une page qui rend `null` le temps de
 * ses requêtes ferait remonter 0, et un iframe de 0px n'est plus rendu par le
 * navigateur : plus aucun ResizeObserver ne se déclenche ensuite.
 */
export const MIN_EMBED_HEIGHT = 320;

/** Demandes de token consécutives au parent avant d'abandonner. */
export const MAX_AUTH_EXPIRED_RETRIES = 3;

/** Le parent a ce délai pour répondre au `ready` avant qu'on affiche l'erreur. */
export const EMBED_HANDSHAKE_TIMEOUT_MS = 10000;

const isEmbedPath = () => {
  const { pathname } = window.location;

  return (
    pathname === EMBED_PATH_PREFIX ||
    pathname.startsWith(`${EMBED_PATH_PREFIX}/`)
  );
};

/** Comparaison de références : ne lève pas sur un parent cross-origin. */
export const isFramed = () => window.self !== window.top;

/**
 * Les deux conditions sont nécessaires. Le chemin, pour qu'un tiers qui frame
 * le portail ne bascule pas le reste de l'app en mode embed. La frame, pour
 * qu'un `/embed/...` ouvert au premier plan (lien partagé, restauration de
 * session, iframe détachée) garde la session persistée et le formulaire de
 * connexion au lieu d'attendre indéfiniment un token qui ne viendra jamais.
 */
export const isEmbedded = () => isEmbedPath() && isFramed();

/**
 * Préfixe un chemin interne pour rester dans la branche /embed du router, en
 * conservant la query string.
 *
 * Le scope de l'embed vit entièrement dans l'URL (`?propertyId=…&status=…`) :
 * sans cette propagation, le premier aller-retour vers l'écran de création ou
 * le détail ramènerait sur une liste sans scope. Le serveur réimpose de toute
 * façon le propertyId du token (restrictEmbedTokenScope), mais la liste
 * afficherait un filtre différent de celui de l'URL.
 */
export const embedPath = (path: string) => {
  if (!isEmbedded()) {
    return path;
  }

  return `${EMBED_PATH_PREFIX}${path}${window.location.search}`;
};

/** Chemin équivalent hors branche /embed, pour sortir du mode embarqué. */
export const unembedPath = (pathname: string) =>
  pathname.startsWith(EMBED_PATH_PREFIX)
    ? pathname.slice(EMBED_PATH_PREFIX.length) || '/incidents'
    : pathname;

export const getAllowedParentOrigins = (): string[] =>
  String(import.meta.env.VITE_EMBED_PARENT_ORIGINS || '')
    .split(',')
    .map((origin) => origin.trim())
    .filter(Boolean);

export const isAllowedParentOrigin = (origin: string) =>
  getAllowedParentOrigins().includes(origin);
